import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Brain, Sparkles } from 'lucide-react';
import { authAiInsightStream } from '../authData';
import { cn } from '../../../utils/cn';
import { authEase } from '../authMotion';
import type { AuthVisualVariant } from '../authVisualVariant';

const insightAccent = {
  light: {
    positive: 'border-emerald-200/80 bg-emerald-50/80',
    alert: 'border-amber-200/80 bg-amber-50/80',
    neutral: 'border-slate-200/80 bg-slate-50/80',
  },
  dark: {
    positive: 'border-emerald-500/20 bg-emerald-500/10',
    alert: 'border-amber-500/20 bg-amber-500/10',
    neutral: 'border-white/10 bg-white/5',
  },
};

const phaseLabel = {
  thinking: 'Analyzing',
  detect: 'Detected',
  complete: 'Updated',
} as const;

function useTypewriter(text: string, active: boolean, reduceMotion: boolean | null) {
  const [display, setDisplay] = useState(text);

  useEffect(() => {
    if (reduceMotion || !active) {
      setDisplay(text);
      return undefined;
    }
    setDisplay('');
    let i = 0;
    const id = window.setInterval(() => {
      i += 1;
      setDisplay(text.slice(0, i));
      if (i >= text.length) window.clearInterval(id);
    }, 22);
    return () => window.clearInterval(id);
  }, [text, active, reduceMotion]);

  return display;
}

interface AuthAIInsightPanelProps {
  variant?: AuthVisualVariant;
  compact?: boolean;
}

export const AuthAIInsightPanel: React.FC<AuthAIInsightPanelProps> = ({
  variant = 'light',
  compact = false,
}) => {
  const dark = variant === 'dark';
  const reduceMotion = useReducedMotion();
  const [index, setIndex] = useState(0);
  const active = authAiInsightStream[index % authAiInsightStream.length]!;
  const typed = useTypewriter(active.text, true, reduceMotion);

  useEffect(() => {
    if (reduceMotion) return undefined;
    const len = active.text.length;
    const dwell = Math.max(3200, len * 24 + 1200);
    const id = window.setInterval(
      () => setIndex((i) => (i + 1) % authAiInsightStream.length),
      dwell
    );
    return () => window.clearInterval(id);
  }, [reduceMotion, active.text]);

  return (
    <div
      className={cn(
        'border-b',
        compact ? 'px-4 py-2.5' : 'px-5 py-4',
        dark ? 'border-white/10' : 'border-slate-100'
      )}
    >
      <p
        className={cn(
          'mb-2 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide',
          dark ? 'text-slate-400' : 'text-slate-400'
        )}
      >
        <Sparkles className={cn('h-3.5 w-3.5', dark ? 'text-blue-400' : 'text-blue-600')} aria-hidden />
        AI insight engine
        <motion.span
          className="ml-auto flex items-center gap-1 text-[10px] font-medium normal-case text-indigo-600"
          animate={reduceMotion ? {} : { opacity: [0.55, 1, 0.55] }}
          transition={{ duration: 2.2, repeat: Infinity }}
        >
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-40" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-indigo-500" />
          </span>
          Live
        </motion.span>
      </p>

      <AnimatePresence mode="wait">
        <motion.div
          key={active.id}
          initial={reduceMotion ? false : { opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          {...(reduceMotion ? {} : { exit: { opacity: 0, y: -6 } })}
          transition={{ duration: 0.35, ease: authEase }}
          className={cn(
            'rounded-xl border px-3 py-2.5',
            insightAccent[variant][active.type]
          )}
        >
          <motion.div
            className="mb-1.5 flex items-center gap-1.5 text-[10px] font-medium text-slate-500"
            initial={reduceMotion ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <Brain className="h-3 w-3 text-indigo-500" aria-hidden />
            {phaseLabel[active.phase]}
          </motion.div>
          <p
            className={cn(
              'text-xs leading-snug',
              compact ? 'min-h-[2rem] line-clamp-3' : 'min-h-[2.5rem] leading-relaxed',
              dark ? 'text-slate-300' : 'text-slate-700'
            )}
          >
            {typed}
            {!reduceMotion && typed.length < active.text.length ? (
              <motion.span
                className="ml-0.5 inline-block h-3 w-px bg-slate-400"
                animate={{ opacity: [1, 0] }}
                transition={{ duration: 0.5, repeat: Infinity }}
                aria-hidden
              />
            ) : null}
          </p>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};
